import { ChangeEvent } from "@mcleod/components";
import { ArrayUtil } from "@mcleod/core";
import { RowPermsAllowDeny } from "../models/ModelPermsAllowDeny";
import { PanelSecureItem, PanelSecureItemProps } from "./PanelSecureItem";
import { SecureItem } from "./SecureItem";
import { UserOrGroup } from "./UserOrGroup";

export class PanelIndividualSecure extends PanelSecureItem implements PanelSecureItemProps {
    private readonly secureItem: SecureItem;
    private allows: RowPermsAllowDeny[];

    constructor(secureItem: SecureItem, props: Partial<PanelSecureItemProps>) {
        super({...props});
        this.secureItem = secureItem;
        this.allows = this.permissionsSource.getAllowDenyRows(this.secureItem) ?? [];
        this.labelSecurityType.text = this.secureItem.permsDef.description;
        this.syncAllowedPanel();
        this.secured = this.permissionsSource.getSecureItemRow(this.secureItem) != null;
        this.syncLabelDescription();
    }

    override syncLabelDescription() {
        const permsDef = this.secureItem.permsDef;
        let description = permsDef.description;

        if (this.secured) {
            const availableToSomeDescription = permsDef.availableToSomeDescription || `${permsDef.availableToNoneDescription} except:`;
            description = this.someAllowed ? availableToSomeDescription : permsDef.availableToNoneDescription;
        } else {
            description = permsDef.availableToAllDescription;
        }
        this.labelDescription.text = description;
    }

    get someAllowed(): boolean {
        return !ArrayUtil.isEmptyArray(this.allows);
    }

    override alreadyAllowed(userOrGroup: UserOrGroup, id: string): boolean {
        if (this.allows != null)
            for (const item of this.allows)
                if (item.get("user_or_group_id") === id && item.get("user_or_group_flag") === userOrGroup)
                    return true;
        return false;
    }

    override isUserAllowedByGroupMembership(userId: string): boolean {
        return this.allows.filter(item => item.get("user_or_group_flag") === "G")
            .some(item => this.permsUserCache.isUserInGroup(userId, item.get("user_or_group_id")));
    }

    protected internalAddAllowed(userOrGroup: UserOrGroup, id: string) {
        const row = this.addAllowedToPermissionsSource(this.secureItem, userOrGroup, id);
        this.allows.push(row);
        this.addAllowedItemPanel(row);
    }

    async switchSecuredOnChange(event: ChangeEvent) {
        if (!event.userInitiatedChange)
            return;
        const secured = event.newValue === true;
        if (secured) {
            this.permissionsSource.addSecureItemRow(this.secureItem);
            this.syncAllowedPanel();
        } else {
            this.permissionsSource.deleteSecureItem(this.secureItem);
        }
        this.secured = secured;
    }

    syncAllowedPanel() {
        if (this.allows != null) {
            this.panelAllowed.removeAll();
            this.allows.forEach(row => this.addAllowedItemPanel(row));
        }
    }

    addAllowedItemPanel(row: RowPermsAllowDeny) {
        const result = this.createAllowItemPanel(
            row,
            () => {
                this.permissionsSource.deleteAllowDenyRow(row);
                ArrayUtil.removeFromArray(this.allows, row);
                this.panelAllowed.remove(result);
            }
        );
        this.panelAllowed.add(result);
    }
}
